import { type ButtonHTMLAttributes, forwardRef } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
}

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  // `bg-primary` kept as a base layer; `btn-primary` overlays the vibrant
  // violet->pink gradient, colored glow, and spring hover/press animation.
  primary:
    'btn-primary bg-primary text-white',
  secondary:
    'bg-secondary-light text-text-primary border border-border hover:bg-secondary hover:text-white active:bg-secondary active:scale-[0.97]',
  ghost:
    'bg-transparent text-text-secondary hover:bg-hover-bg hover:text-text-primary active:bg-bg-secondary active:scale-[0.97]',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-body',
  lg: 'px-6 py-3 text-body',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className = '', children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={[
          'inline-flex items-center justify-center font-medium',
          'rounded-sm',
          'transition-all duration-default ease-default',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:pointer-events-none',
          variantClasses[variant],
          sizeClasses[size],
          className,
        ].join(' ')}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
